import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import XIcon from 'icons/XIcon.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import utils from 'utils';

import './PopNotification.styles.scss';

type NotificationType = 'Success' | 'Error' | 'Info';

export type PopNotificationProps = {
  className?: string;
  notId?: string;
  side?:
    | 'top'
    | 'top-right'
    | 'top-left'
    | 'bottom'
    | 'bottom-right'
    | 'bottom-left';
  closable?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onCtaClick?: () => Promise<void>;
  onClose?: () => void;
  dissappearAfter?: number;
  children?: React.ReactNode;
  type?: NotificationType;
};

export type PopNotificationRef = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NOTIFICATION_DURATION_TIME_MS = 20000;

const PopNotification: React.ForwardRefRenderFunction<
  PopNotificationRef,
  PopNotificationProps
> = (props, ref) => {
  const [open, setOpen] = useState(true);
  const isPhablet = useMatchMedia('isPhablet');

  const {
    className,
    side = isPhablet ? 'bottom' : 'bottom right',
    closable = true,
    onClick = utils.noop,
    onClose,
    dissappearAfter = NOTIFICATION_DURATION_TIME_MS,
    type = 'Info',
    children,
  } = props;

  const timeoutRef = useRef<NodeJS.Timeout>();
  const classes = classNames(
    'pop-notification',
    { 'pop-notification--error': type === 'Error' },
    { 'pop-notification--success': type === 'Success' },
    { 'pop-notification--info': type === 'Info' },
    side.split('-').join(' '),
    className,
  );

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, dissappearAfter);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [dissappearAfter]);

  const refVal = useRef<PopNotificationRef>({ open, setOpen });

  useImperativeHandle(
    ref,
    () => {
      refVal.current.open = open;
      return refVal.current;
    },
    [open],
  );

  return (
    <CSSTransition
      timeout={250}
      in={open}
      classNames={{
        appear: 'pop-notification-transition-appear',
        appearActive: 'pop-notification-transition-appear-active',
        appearDone: 'pop-notification-transition-appear-done',
        enter: 'pop-notification-transition-enter',
        enterActive: 'pop-notification-transition-enter-active',
        enterDone: 'pop-notification-transition-enter-done',
        exit: 'pop-notification-transition-exit',
        exitActive: 'pop-notification-transition-exit-active',
        exitDone: 'pop-notification-transition-exit-done',
      }}
      exit
      appear
      unmountOnExit
      onExited={() => {
        clearTimeout(timeoutRef.current);
        onClose?.();
      }}
    >
      <div
        className={classes}
        onClick={(ev) => {
          onClick?.(ev);
          if (closable) {
            clearTimeout(timeoutRef.current);
          }
        }}
      >
        {closable && (
          <button
            className="pop-notification__close"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <XIcon />
          </button>
        )}
        {children}
      </div>
    </CSSTransition>
  );
};

export default forwardRef(PopNotification);
